// CloudFront signed cookies for private S3 assets: the browser half. The
// signing is @auger/storage's signAssetCookies, behind the
// aws-cloudfront-set-cookies function. Provider-neutral: the auth service calls
// setCloudFrontCookies once at boot and hands scheduleCloudFrontRefresh its
// getIdToken.

import { ASSET_COOKIE_MAX_AGE_S } from "@auger/storage/rules"

// Rethrows on failure — callers decide how to handle it (resolveSession's boot
// call is fire-and-forget; scheduleCloudFrontRefresh below uses the rejection
// to drive its retry/backoff).
export async function setCloudFrontCookies(token: string): Promise<void> {
  try {
    const response = await fetch("/.netlify/functions/aws-cloudfront-set-cookies", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "X-Requested-With": "XmlHttpRequest",
        "Content-Type": "application/json",
      },
      credentials: "include",
    })
    if (!response.ok) throw new Error(`CloudFront cookie request failed: ${response.status}`)
  } catch (error) {
    console.error("Failed to set CloudFront cookies:", error)
    throw error
  }
}

// Cookies expire on a fixed Max-Age (ASSET_COOKIE_MAX_AGE_S), independent of
// the token lifetime (Stytch session JWTs: 5 minutes, refreshed by the SDK) —
// so a tab left open past it needs its own re-mint regardless of how token
// refresh is going. The boot-time call in resolveSession() only fires once.
// Re-mint an hour before they lapse.
const CLOUDFRONT_REFRESH_INTERVAL_MS = (ASSET_COOKIE_MAX_AGE_S - 60 * 60) * 1000
const CLOUDFRONT_RETRY_DELAYS_MS = [1000, 2000, 4000] // mirrors currentUser's boot-retry shape

export function scheduleCloudFrontRefresh(
  getToken: () => Promise<string | null>,
  attempt = 0
): void {
  const delay =
    attempt === 0 ? CLOUDFRONT_REFRESH_INTERVAL_MS : CLOUDFRONT_RETRY_DELAYS_MS[attempt - 1]
  setTimeout(async () => {
    try {
      const token = await getToken()
      if (!token) return // logged out; nothing to refresh, don't reschedule
      await setCloudFrontCookies(token)
      scheduleCloudFrontRefresh(getToken, 0)
    } catch (error) {
      if (attempt < CLOUDFRONT_RETRY_DELAYS_MS.length) {
        console.error("CloudFront cookie refresh failed, retrying:", error)
        scheduleCloudFrontRefresh(getToken, attempt + 1)
      } else {
        console.error(
          "CloudFront cookie refresh: exhausted retries, resuming on the normal 23h cadence",
          error
        )
        scheduleCloudFrontRefresh(getToken, 0)
      }
    }
  }, delay)
}
